import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle, Users, Wallet, Landmark, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { chf, moisCourant, periodeLabel } from "./salairesUtils";

const KPI = ({ label, value, sub, icon: Icon, tone = "gray", testid }) => {
  const tones = {
    green: "border-emerald-200 from-emerald-50",
    amber: "border-amber-200 from-amber-50",
    blue: "border-blue-200 from-blue-50",
    gray: "border-gray-200 from-gray-50",
  };
  return (
    <Card className={`p-4 bg-gradient-to-br to-transparent ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
          <div className="text-2xl font-bold text-gray-900 mt-1 font-mono">{value}</div>
          {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
        </div>
        {Icon && <Icon className="w-6 h-6 text-gray-300 shrink-0" />}
      </div>
    </Card>
  );
};

export default function SalairesDashboardTab() {
  const [from, setFrom] = useState(moisCourant());
  const [to, setTo] = useState(moisCourant());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/salaires/dashboard", {
        params: { periode_from: from, periode_to: to },
      });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div className="space-y-5" data-testid="salaires-dashboard">
      <Card className="p-4 border-gray-200">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Du mois</Label>
            <Input type="month" value={from} onChange={(e) => setFrom(e.target.value)}
                   className="mt-1 w-40" data-testid="dashboard-periode-from" />
          </div>
          <div>
            <Label className="text-xs">Au mois</Label>
            <Input type="month" value={to} onChange={(e) => setTo(e.target.value)}
                   className="mt-1 w-40" data-testid="dashboard-periode-to" />
          </div>
          <div className="text-xs text-gray-500 pb-2">
            {data ? `${data.nb_fiches} fiche(s) sur la période` : ""}
          </div>
        </div>
      </Card>

      {loading && <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPI label="Collaborateurs actifs" value={data.collaborateurs_actifs} icon={Users} testid="kpi-collaborateurs" />
            <KPI label="Masse salariale brute" value={chf(data.masse_salariale_brute)} icon={TrendingUp} tone="blue" testid="kpi-brut" />
            <KPI label="Salaires nets" value={chf(data.salaires_nets)} icon={Wallet} tone="green" testid="kpi-net" />
            <KPI label="Coût total employeur" value={chf(data.cout_total_employeur)}
                 sub={`dont ${chf(data.charges_patronales)} de charges`} icon={Landmark} testid="kpi-cout" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPI label="Salaires à payer" value={chf(data.salaires_a_payer)} tone="amber" testid="kpi-a-payer" />
            <KPI label="Charges sociales à payer" value={chf(data.charges_sociales_a_payer)} tone="amber" testid="kpi-charges-a-payer" />
            <KPI label="Paiements rapprochés" value={chf(data.paiements_rapproches)} tone="green" testid="kpi-rapproches" />
            <KPI label="Cotisations employé" value={chf(data.cotisations_employe)} testid="kpi-cotisations" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-4 border-gray-200">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Détail par rubrique</h3>
              <table className="w-full text-sm" data-testid="dashboard-rubriques">
                <thead className="text-left text-[11px] uppercase text-gray-500">
                  <tr><th className="py-1">Rubrique</th><th className="text-right">Part employé</th><th className="text-right">Part employeur</th></tr>
                </thead>
                <tbody>
                  {Array.from(new Set([
                    ...Object.keys(data.par_rubrique?.retenues || {}),
                    ...Object.keys(data.par_rubrique?.charges || {}),
                  ])).map((code) => (
                    <tr key={code} className="border-t border-gray-100">
                      <td className="py-1.5 uppercase text-xs text-gray-700">{code}</td>
                      <td className="text-right font-mono">{chf(data.par_rubrique?.retenues?.[code] || 0)}</td>
                      <td className="text-right font-mono">{chf(data.par_rubrique?.charges?.[code] || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            <Card className="p-4 border-gray-200">
              <h3 className="font-heading font-semibold text-gray-900 mb-3">Statuts des fiches</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(data.statuts || {}).map(([s, n]) => (
                  <span key={s} className="text-xs px-2 py-1 rounded-md bg-gray-100 text-gray-700" data-testid={`statut-${s}`}>
                    {s} : <b>{n}</b>
                  </span>
                ))}
                {!Object.keys(data.statuts || {}).length && (
                  <span className="text-xs text-gray-400">Aucune fiche sur la période</span>
                )}
              </div>
              <h3 className="font-heading font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-500" /> Anomalies
              </h3>
              {(data.anomalies || []).length === 0 ? (
                <p className="text-xs text-gray-400">Aucune anomalie détectée</p>
              ) : (
                <ul className="space-y-1" data-testid="dashboard-anomalies">
                  {data.anomalies.map((a, i) => (
                    <li key={i} className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      <b>{periodeLabel(a.periode)}</b> · {a.employe} — {a.message}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
